import React, { useState, useEffect, useCallback } from 'react';
import { fetchNotifications } from '../../../services/apiService';

const TYPE_CONFIG = {
  new_booking: {
    icon: '✅',
    label: 'Neue Buchung',
    bg: 'rgba(5,150,105,0.08)',
    border: 'rgba(5,150,105,0.2)',
    color: '#059669',
    dot: '#059669',
  },
  step_abandoned: {
    icon: '⚠️',
    label: 'Abbruch',
    bg: 'rgba(245,158,11,0.08)',
    border: 'rgba(245,158,11,0.2)',
    color: '#d97706',
    dot: '#d97706',
  },
  slot_conflict: {
    icon: '🔴',
    label: 'Slot-Konflikt',
    bg: 'rgba(239,68,68,0.08)',
    border: 'rgba(239,68,68,0.2)',
    color: '#dc2626',
    dot: '#dc2626',
  },
  calendar_event_created: {
    icon: '📅',
    label: 'Outlook-Event',
    bg: 'rgba(37,99,235,0.08)',
    border: 'rgba(37,99,235,0.2)',
    color: '#2563eb',
    dot: '#2563eb',
  },
  graph_api_error: {
    icon: '🔌',
    label: 'Graph API Fehler',
    bg: 'rgba(220,38,38,0.08)',
    border: 'rgba(220,38,38,0.25)',
    color: '#b91c1c',
    dot: '#b91c1c',
  },
};

const STATUS_COLORS = {
  confirmed: { bg: 'rgba(5,150,105,0.1)', color: '#059669' },
  rescheduled: { bg: 'rgba(245,158,11,0.1)', color: '#d97706' },
  cancelled: { bg: 'rgba(239,68,68,0.1)', color: '#dc2626' },
};

const NotificationCenter = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [limitHours, setLimitHours] = useState(72);
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const data = await fetchNotifications(limitHours);
    setNotifications(data || []);
    setLastRefresh(new Date());
    setLoading(false);
  }, [limitHours]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 60s
  useEffect(() => {
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, [load]);

  const filtered = filter === 'all' ? notifications : notifications?.filter(n => n?.type === filter);

  const counts = {
    all: notifications?.length,
    new_booking: notifications?.filter(n => n?.type === 'new_booking')?.length,
    step_abandoned: notifications?.filter(n => n?.type === 'step_abandoned')?.length,
    slot_conflict: notifications?.filter(n => n?.type === 'slot_conflict')?.length,
    calendar_event_created: notifications?.filter(n => n?.type === 'calendar_event_created')?.length,
    graph_api_error: notifications?.filter(n => n?.type === 'graph_api_error')?.length,
  };

  const formatTs = (ts) => {
    if (!ts) return '–';
    try {
      const d = new Date(ts);
      const now = new Date();
      const diffMs = now - d;
      const diffMin = Math.floor(diffMs / 60000);
      const diffH = Math.floor(diffMs / 3600000);
      const diffD = Math.floor(diffMs / 86400000);
      if (diffMin < 1) return 'Gerade eben';
      if (diffMin < 60) return `vor ${diffMin} Min.`;
      if (diffH < 24) return `vor ${diffH} Std.`;
      if (diffD < 7) return `vor ${diffD} Tag${diffD > 1 ? 'en' : ''}`;
      return d?.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return ts; }
  };

  return (
    <div style={{
      background: 'white', borderRadius: '20px',
      border: '1px solid #dde1e7',
      boxShadow: '0 4px 6px rgba(62,73,84,0.04), 0 10px 20px rgba(62,73,84,0.06)',
      overflow: 'hidden', marginBottom: '28px',
    }}>
      {/* Header */}
      <div style={{ padding: '20px 24px', borderBottom: '1px solid #dde1e7', background: '#1e2b38' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
              </svg>
            </div>
            <div>
              <h2 style={{ fontFamily: 'var(--font-ui)', fontSize: '16px', fontWeight: 700, color: 'white', margin: 0 }}>Benachrichtigungen</h2>
              <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.55)', fontFamily: 'var(--font-ui)', margin: 0 }}>
                {lastRefresh ? `Aktualisiert: ${lastRefresh?.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}` : 'Wird geladen…'}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <select
              value={limitHours}
              onChange={e => setLimitHours(Number(e?.target?.value))}
              style={{ height: '34px', padding: '0 10px', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', fontSize: '12px', background: 'rgba(255,255,255,0.1)', color: 'white', cursor: 'pointer', fontFamily: 'var(--font-ui)', outline: 'none' }}
            >
              <option value={24} style={{ background: '#1e2b38' }}>Letzte 24h</option>
              <option value={72} style={{ background: '#1e2b38' }}>Letzte 72h</option>
              <option value={168} style={{ background: '#1e2b38' }}>Letzte 7 Tage</option>
              <option value={720} style={{ background: '#1e2b38' }}>Letzter Monat</option>
            </select>
            <button
              onClick={load}
              disabled={loading}
              style={{ height: '34px', padding: '0 12px', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', background: 'rgba(255,255,255,0.1)', color: 'white', cursor: loading ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '5px', transition: 'all 150ms' }}
              onMouseEnter={e => { if (!loading) e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }}>
                <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
              </svg>
              {loading ? 'Lädt…' : 'Aktualisieren'}
            </button>
          </div>
        </div>

        {/* Filter tabs */}
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {[
            { key: 'all', label: 'Alle', count: counts?.all },
            { key: 'new_booking', label: '✅ Buchungen', count: counts?.new_booking },
            { key: 'calendar_event_created', label: '📅 Outlook', count: counts?.calendar_event_created },
            { key: 'step_abandoned', label: '⚠️ Abbrüche', count: counts?.step_abandoned },
            { key: 'slot_conflict', label: '🔴 Konflikte', count: counts?.slot_conflict },
            { key: 'graph_api_error', label: '🔌 API Fehler', count: counts?.graph_api_error },
          ]?.map(tab => (
            <button
              key={tab?.key}
              onClick={() => setFilter(tab?.key)}
              style={{
                padding: '5px 12px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                fontSize: '12px', fontWeight: 600, fontFamily: 'var(--font-ui)',
                transition: 'all 150ms',
                background: filter === tab?.key ? 'white' : 'rgba(255,255,255,0.1)',
                color: filter === tab?.key ? '#1e2b38' : 'rgba(255,255,255,0.75)',
                display: 'flex', alignItems: 'center', gap: '5px',
              }}
            >
              {tab?.label}
              <span style={{
                background: filter === tab?.key ? '#1e2b38' : 'rgba(255,255,255,0.2)',
                color: filter === tab?.key ? 'white' : 'rgba(255,255,255,0.9)',
                borderRadius: '100px', padding: '1px 7px', fontSize: '10px', fontWeight: 700,
              }}>{tab?.count}</span>
            </button>
          ))}
        </div>
      </div>
      {/* Notification List */}
      <div style={{ maxHeight: '420px', overflowY: 'auto' }}>
        {loading && filtered?.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#6b7a8d', fontSize: '13px' }}>⏳ Benachrichtigungen werden geladen…</div>
        ) : filtered?.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#6b7a8d', fontSize: '13px' }}>
            <div style={{ fontSize: '28px', marginBottom: '8px' }}>🔔</div>
            Keine Benachrichtigungen im gewählten Zeitraum.
          </div>
        ) : (
          filtered?.map((n, i) => {
            const cfg = TYPE_CONFIG?.[n?.type] || TYPE_CONFIG?.new_booking;
            const sc = n?.status ? STATUS_COLORS?.[n?.status] : null;
            return (
              <div
                key={n?.id}
                style={{
                  padding: '14px 20px',
                  borderBottom: i < filtered?.length - 1 ? '1px solid #f0f1f3' : 'none',
                  display: 'flex', gap: '14px', alignItems: 'flex-start',
                  transition: 'background 150ms',
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#fafbfc'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                {/* Icon */}
                <div style={{
                  width: '36px', height: '36px', borderRadius: '10px', flexShrink: 0,
                  background: cfg?.bg, border: `1px solid ${cfg?.border}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '16px',
                }}>
                  {cfg?.icon}
                </div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: '#1e2b38' }}>{n?.title}</span>
                    <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '100px', background: cfg?.bg, color: cfg?.color, border: `1px solid ${cfg?.border}` }}>
                      {cfg?.label}
                    </span>
                    {sc && n?.status && (
                      <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '100px', background: sc?.bg, color: sc?.color }}>
                        {n?.status === 'confirmed' ? 'Bestätigt' : n?.status === 'rescheduled' ? 'Verschoben' : 'Storniert'}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7a8d', marginTop: '3px' }}>{n?.detail}</div>
                  {n?.email && (
                    <div style={{ fontSize: '11px', color: '#3E4954', marginTop: '2px', opacity: 0.75 }}>{n?.email}</div>
                  )}
                  {n?.berater && (
                    <div style={{ fontSize: '11px', color: '#2563eb', marginTop: '2px', opacity: 0.85 }}>👤 {n?.berater}</div>
                  )}
                  {n?.booking_ref && (
                    <div style={{ fontSize: '10px', color: '#6b7a8d', marginTop: '2px', fontFamily: 'monospace' }}>Ref: {n?.booking_ref}</div>
                  )}
                </div>

                {/* Timestamp */}
                <div style={{ fontSize: '11px', color: '#6b7a8d', whiteSpace: 'nowrap', flexShrink: 0, marginTop: '2px' }}>
                  {formatTs(n?.timestamp)}
                </div>
              </div>
            );
          })
        )}
      </div>
      {filtered?.length > 0 && (
        <div style={{ padding: '12px 20px', borderTop: '1px solid #f0f1f3', background: '#fafbfc', textAlign: 'center', fontSize: '12px', color: '#6b7a8d' }}>
          {filtered?.length} Einträge — Auto-Aktualisierung alle 60 Sekunden
        </div>
      )}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default NotificationCenter;
